import React, { useState } from "react";
import { TableHeaderItem } from "../../../hooks/useTable";
import styled from "styled-components";
import { DeleteIcon } from "../../atoms/icons/DeleteIcon";
import { Checkbox } from "../../atoms/Checkbox";
import { EditIcon } from "../../atoms/icons/EditIcon";
import { createPortal } from "react-dom";
import { useResponsive } from "../../../hooks/useResponsive";

type Props<DataType> = {
  headers: TableHeaderItem<DataType>[];
  data: DataType;
  selectable?: boolean;
  editable?: boolean;
  deletable?: boolean;
  downloadable?: boolean;
  enableCSVDownload?: boolean;
  selected?: boolean;
  enableHoverPopup?: boolean;
  columnWidths?: string[]; // New prop for custom column widths
  onEdit?: (data: DataType) => void;
  onDelete?: (data: DataType) => void;
  onSelect?: (data: DataType, checked: boolean) => void;
};

export const TableRow = <
  DataType extends {
    selected?: boolean;
    disabled?: boolean;
    disabledDownload?: boolean;
    disabledDownloadCSV?: boolean;
    invalid?: boolean;
  }
>({
  headers,
  data,
  selectable,
  editable,
  onEdit,
  onSelect,
  onDelete,
  deletable,
  downloadable,
  enableHoverPopup,
  columnWidths,
}: Props<DataType>) => {
  const [isHovered, setIsHovered] = useState(false);
  const [popupPosition, setPopupPosition] = useState({
    top: 0,
    left: 0,
    showAbove: false,
  });
  const { isMobile, isTablet } = useResponsive();

  const handleMouseEnter = (event: React.MouseEvent<HTMLTableRowElement>) => {
    if (!enableHoverPopup) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const popupHeight = 40 * headers.length + 32;
    const spaceBelow = window.innerHeight - rect.bottom;
    const showAbove = spaceBelow < popupHeight;

    setPopupPosition({
      top: showAbove
        ? rect.top + window.scrollY - popupHeight - 10
        : rect.bottom + window.scrollY + 10,
      left: window.innerWidth / 2,
      showAbove,
    });

    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    if (enableHoverPopup) {
      setIsHovered(false);
    }
  };

  return (
    <>
      <StyledRowWrapper
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        $isHovered={isHovered}
        $invalid={data.invalid}
        $isMobile={isMobile}
      >
        {selectable && (
          <td style={{ width: "24px" }}>
            <Checkbox
              name=""
              variant="empty"
              checked={data.selected}
              onChange={(checked) => onSelect?.(data, checked)}
              disabled={data.disabled}
            />
          </td>
        )}
        {headers.map((header, i) => {
          const rowData = header.dataMapFunction
            ? header.dataMapFunction(data)
            : (data as any)[header.dataKey];

          const isLastColumn = i === headers.length - 1;

          return (
            <StyledTableData
              key={`table-data-${i}`}
              $width={columnWidths?.[i] || header.width || "auto"}
              $i={i}
              $isMobile={isMobile}
            >
              {header.customComponent ? (
                <header.customComponent {...rowData} />
              ) : (
                <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%"
                }}>
                  <span>
                    {typeof rowData === "object" && rowData !== null
                      ? rowData.text || JSON.stringify(rowData)
                      : rowData}
                  </span>
                  {isLastColumn && (editable || deletable) && (
                    <div style={{
                      display: "flex",
                      gap: isMobile ? "12px" : "8px",
                      alignItems: "center",
                      marginLeft: isMobile ? "8px" : "16px"
                    }}>
                      {editable && (
                        <StyledEditButton onClick={() => onEdit?.(data)} $isMobile={isMobile}>
                          <EditIcon />
                        </StyledEditButton>
                      )}
                      {deletable && (
                        <StyledDeleteButton
                          $disabled={data.disabled}
                          onClick={() => !data.disabled && onDelete?.(data)}
                          $isMobile={isMobile}
                        >
                          <DeleteIcon />
                        </StyledDeleteButton>
                      )}
                    </div>
                  )}
                </div>
              )}
            </StyledTableData>
          );
        })}

        {/* Remove separate action columns */}

        {deletable && downloadable && (
          <td
            style={{
              display: "flex",
              gap: "16px",
              alignItems: "center",
              width: "350px",
            }}
          >
            {deletable && (
              <StyledDeleteButton
                $disabled={data.disabled}
                onClick={() => !data.disabled && onDelete?.(data)}
              >
                <DeleteIcon />
              </StyledDeleteButton>
            )}
          </td>
        )}
      </StyledRowWrapper>

      {enableHoverPopup &&
        isHovered &&
        createPortal(
          <StyledPopupContainer
            style={{ top: popupPosition.top, left: popupPosition.left }}
            $showAbove={popupPosition.showAbove}
            $isMobile={isMobile}
          >
            {headers.map((header, index) => {
              const value = header.dataMapFunctionForPopup
                ? header.dataMapFunctionForPopup(data)
                : header.dataMapFunction
                  ? header.dataMapFunction(data)
                  : // @ts-expect-error
                  data[header.dataKey];

              return (
                <StyledPopupContent key={index}>
                  <StyledPopupLabel>
                    {header.popupTitle}
                  </StyledPopupLabel>
                  <StyledPopupValue>
                    {typeof value === "string" ? value : value?.text ?? ""}
                  </StyledPopupValue>
                </StyledPopupContent>
              );
            })}
          </StyledPopupContainer>,
          document.body
        )}
    </>
  );
};

const StyledRowWrapper = styled.tr<{ $isHovered: boolean; $invalid?: boolean; $isMobile?: boolean }>`
  background-color: ${({ $invalid }) => ($invalid ? "#FBFBFB" : "white")};
  transition: background 0.2s ease-in-out, transform 0.2s ease-in-out,
    box-shadow 0.2s ease-in-out;

  ${({ $isHovered, $isMobile }) =>
    $isHovered &&
    `
      transform: ${$isMobile ? 'scale(1.01)' : 'scale(1.02)'};
      box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.08); 
  `}
  
  ${({ $isMobile }) =>
    $isMobile
      ? `
      -webkit-overflow-scrolling: touch;
      `
      : ""}
`;

const StyledPopupContainer = styled.div<{ $showAbove: boolean; $isMobile?: boolean }>`
  position: fixed;
  background-color: #ffffff;
  padding: ${({ $isMobile }) => $isMobile ? '12px' : '16px'};
  border-radius: ${({ $isMobile }) => $isMobile ? '8px' : '10px'};
  box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.2);
  max-width: ${({ $isMobile }) => $isMobile ? '320px' : '380px'};
  font-size: ${({ $isMobile }) => $isMobile ? '13px' : '14px'};
  color: #333;
  z-index: 100;
  transition: opacity 0.2s ease-in-out;
  transform: translateX(-50%);
  left: 50%;

  &:before {
    content: "";
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    border-width: ${({ $isMobile }) => $isMobile ? '10px' : '12px'};
    border-style: solid;
    border-color: ${({ $showAbove }) =>
    $showAbove
      ? "white transparent transparent transparent"
      : "transparent transparent white transparent"};
    top: ${({ $showAbove }) => ($showAbove ? "100%" : "unset")};
    bottom: ${({ $showAbove }) => ($showAbove ? "unset" : "100%")};
  }
`;

const StyledPopupContent = styled.div`
  display: grid;
  grid-template-columns: 100px auto;
  row-gap: 8px;
`;

const StyledPopupLabel = styled.div`
  font-weight: 500;
  color: #666;
  line-height: 2.5;
`;

const StyledPopupValue = styled.div`
  font-weight: 400;
  color: #333;
  line-height: 2.5;
  word-wrap: break-word;
  white-space: normal;
  overflow-wrap: break-word;
  max-width: 100%;
`;

const StyledDeleteButton = styled.button<{ $disabled?: boolean; $isMobile?: boolean }>`
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  border: none;
  background: none;
  padding: ${({ $isMobile }) => $isMobile ? '8px' : '4px'};
  border-radius: ${({ $isMobile }) => $isMobile ? '4px' : '2px'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ $isMobile }) => $isMobile ? 'rgba(0, 0, 0, 0.05)' : 'transparent'};
  }
  
  &:active {
    background-color: ${({ $isMobile }) => $isMobile ? 'rgba(0, 0, 0, 0.1)' : 'transparent'};
  }
`;

const StyledEditButton = styled.button<{ $isMobile?: boolean }>`
  cursor: pointer;
  border: none;
  background: none;
  padding: ${({ $isMobile }) => $isMobile ? '8px' : '4px'};
  border-radius: ${({ $isMobile }) => $isMobile ? '4px' : '2px'};
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: ${({ $isMobile }) => $isMobile ? 'rgba(0, 0, 0, 0.05)' : 'transparent'};
  }
  
  &:active {
    background-color: ${({ $isMobile }) => $isMobile ? 'rgba(0, 0, 0, 0.1)' : 'transparent'};
  }
`;

const StyledTableData = styled.td<{
  $width?: string;
  $invalid?: boolean;
  $i: number;
  $isMobile?: boolean;
}>`
  width: ${({ $width }) => $width || "200px"};
  max-width: ${({ $width }) => $width || "200px"};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-all;
  word-wrap: break-word;
  padding: ${({ $isMobile }) => $isMobile ? '6px 12px' : '8px 16px'};
  height: ${({ $isMobile }) => $isMobile ? '28px' : '30px'};
  ${({ $invalid, $i }) =>
    $invalid &&
    $i === 0 &&
    `
      display: flex;
      align-items: center;
      `}
`;
